import { useEffect, useState } from "react";
import {
  Mail, MessageSquare, Loader2, Send, Save, AlertTriangle, Check, X, RefreshCcw, ClipboardList, Star, Sparkles,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import axios from "axios";
import { toast } from "sonner";
import AdminSidebar from "../../components/admin/AdminSidebar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DELAY_OPTIONS = [
  { value: 0, label: "Immediately after return" },
  { value: 24, label: "1 day after return" },
  { value: 48, label: "2 days after return" },
];

export default function AdminReviewRequests() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, sent: 0, failed: 0 });
  const [loadingList, setLoadingList] = useState(true);
  const [filter, setFilter] = useState("all");
  const [manualBookingId, setManualBookingId] = useState("");
  const [manualSending, setManualSending] = useState(false);
  const [ticking, setTicking] = useState(false);
  // Type selector: "review" (default) or "trial"
  const [requestType, setRequestType] = useState("review");
  // Templates sub-tab: which template block is currently editable
  const [templateTab, setTemplateTab] = useState("review");

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("adminToken")}` });

  const loadAll = async () => {
    try {
      const [s, r] = await Promise.all([
        axios.get(`${API}/admin/review-requests/settings`, { headers: headers() }),
        axios.get(`${API}/admin/review-requests${filter !== "all" ? `?status=${filter}` : ""}`, { headers: headers() }),
      ]);
      setSettings(s.data);
      setRows(r.data.rows || []);
      setCounts(r.data.counts || { pending: 0, sent: 0, failed: 0 });
    } catch {
      toast.error("Failed to load review-request settings");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-line */ }, [filter]);

  const saveSettings = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const body = {
        enabled_email: !!settings.enabled_email,
        enabled_sms: !!settings.enabled_sms,
        delay_hours: parseInt(settings.delay_hours, 10),
        email_subject: settings.email_subject || "",
        email_body: settings.email_body || "",
        sms_body: settings.sms_body || "",
        review_link_template: settings.review_link_template || "",
        // Trial templates
        trial_enabled_email: !!settings.trial_enabled_email,
        trial_enabled_sms: !!settings.trial_enabled_sms,
        trial_email_subject: settings.trial_email_subject || "",
        trial_email_body: settings.trial_email_body || "",
        trial_sms_body: settings.trial_sms_body || "",
        trial_link_template: settings.trial_link_template || "",
      };
      const res = await axios.patch(`${API}/admin/review-requests/settings`, body, { headers: headers() });
      setSettings(res.data);
      toast.success("Settings saved");
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const sendManual = async (identifier, channels) => {
    if (!identifier) {
      toast.error(requestType === "trial" ? "Please enter a trial lead ID" : "Please enter a booking ID");
      return;
    }
    setManualSending(true);
    try {
      const res = await axios.post(`${API}/admin/review-requests/${identifier}/send`,
        { channels, type: requestType }, { headers: headers() });
      const r = res.data.results || {};
      const summary = Object.entries(r).map(([c, v]) => `${c}: ${v.ok ? "✓ sent" : "✗ " + (v.error || "failed")}`).join(" · ");
      const label = requestType === "trial" ? "Trial" : "Booking";
      toast.success(`${label} ${identifier} — ${summary}`);
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Manual send failed");
    } finally {
      setManualSending(false);
    }
  };

  const tickNow = async () => {
    setTicking(true);
    try {
      const res = await axios.post(`${API}/admin/review-requests/tick`, {}, { headers: headers() });
      toast.success(`Swept ${res.data.checked} due rows — ${res.data.sent} sent, ${res.data.failed} failed`);
      await loadAll();
    } catch {
      toast.error("Sweep failed");
    } finally {
      setTicking(false);
    }
  };

  // Placeholder / hint text swap depending on active editing tab
  const isTrialTab = templateTab === "trial";

  return (
    <div className="min-h-screen bg-[#FAF8F0]">
      <AdminSidebar />
      <main className="lg:ml-64 p-6 md:p-10">
        <div className="max-w-[900px] mx-auto">
          <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
            <div>
              <h1
                className="text-2xl text-[#282828]"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
                data-testid="admin-review-requests-title"
              >
                Review Requests
              </h1>
              <p className="text-[12px] text-[#676664]/50 mt-1">
                Automated email + SMS after each completed booking or trial. Manual triggers and templates below.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={tickNow}
                disabled={ticking}
                className="text-[10px] tracking-[0.12em] uppercase border-[#282828]/12 text-[#676664]"
                data-testid="tick-sweep-btn"
              >
                {ticking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><RefreshCcw className="w-3 h-3 mr-1.5" /> Run sweep now</>}
              </Button>
            </div>
          </div>

          {!settings ? (
            <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#9A7B4F]" /></div>
          ) : (
            <>
              {/* ─── Settings card ─── */}
              <div className="bg-white border border-[#282828]/8 p-5 mb-6">
                <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                  <h2 className="text-[14px] text-[#282828] uppercase tracking-[0.08em] font-medium">Settings</h2>
                  {/* Template type sub-tabs */}
                  <div className="inline-flex bg-[#FAF8F0] border border-[#282828]/10 p-1">
                    <button
                      type="button"
                      onClick={() => setTemplateTab("review")}
                      className={`px-3 py-1 text-[10px] tracking-[0.08em] uppercase flex items-center gap-1.5 transition-all ${templateTab === "review" ? "bg-[#9A7B4F] text-white" : "text-[#676664]/70 hover:text-[#282828]"}`}
                      data-testid="template-tab-review"
                    >
                      <Star className="w-3 h-3" /> Review Request
                    </button>
                    <button
                      type="button"
                      onClick={() => setTemplateTab("trial")}
                      className={`px-3 py-1 text-[10px] tracking-[0.08em] uppercase flex items-center gap-1.5 transition-all ${templateTab === "trial" ? "bg-[#9A7B4F] text-white" : "text-[#676664]/70 hover:text-[#282828]"}`}
                      data-testid="template-tab-trial"
                    >
                      <Sparkles className="w-3 h-3" /> Trial Request
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  {!isTrialTab ? (
                    <>
                      {/* Review Request toggles */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <label className="flex items-center gap-2 cursor-pointer border border-[#282828]/8 p-3 hover:border-[#9A7B4F]/30">
                          <input
                            type="checkbox"
                            checked={!!settings.enabled_email}
                            onChange={(e) => setSettings({ ...settings, enabled_email: e.target.checked })}
                            data-testid="toggle-email"
                            className="w-4 h-4 accent-[#9A7B4F]"
                          />
                          <Mail className="w-4 h-4 text-[#676664]" />
                          <span className="text-[13px] text-[#282828]">Send review-request email</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer border border-[#282828]/8 p-3 hover:border-[#9A7B4F]/30">
                          <input
                            type="checkbox"
                            checked={!!settings.enabled_sms}
                            onChange={(e) => setSettings({ ...settings, enabled_sms: e.target.checked })}
                            data-testid="toggle-sms"
                            className="w-4 h-4 accent-[#9A7B4F]"
                          />
                          <MessageSquare className="w-4 h-4 text-[#676664]" />
                          <span className="text-[13px] text-[#282828]">Send review-request SMS</span>
                        </label>
                      </div>

                      <div>
                        <label className="block text-[11px] uppercase tracking-[0.08em] text-[#676664]/60 mb-1.5">When to send</label>
                        <select
                          value={settings.delay_hours}
                          onChange={(e) => setSettings({ ...settings, delay_hours: parseInt(e.target.value, 10) })}
                          className="w-full border border-[#282828]/12 px-3 py-2 text-[13px] bg-white"
                          data-testid="delay-select"
                        >
                          {DELAY_OPTIONS.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-[11px] uppercase tracking-[0.08em] text-[#676664]/60 mb-1.5">Email subject</label>
                        <Input
                          value={settings.email_subject || ""}
                          onChange={(e) => setSettings({ ...settings, email_subject: e.target.value })}
                          className="text-[13px]"
                          data-testid="email-subject"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] uppercase tracking-[0.08em] text-[#676664]/60 mb-1.5">Email body</label>
                        <textarea
                          rows={6}
                          value={settings.email_body || ""}
                          onChange={(e) => setSettings({ ...settings, email_body: e.target.value })}
                          className="w-full px-3 py-2.5 border border-[#282828]/12 text-[13px] text-[#282828] resize-y bg-transparent"
                          data-testid="email-body"
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] uppercase tracking-[0.08em] text-[#676664]/60 mb-1.5">SMS body</label>
                        <textarea
                          rows={3}
                          value={settings.sms_body || ""}
                          onChange={(e) => setSettings({ ...settings, sms_body: e.target.value })}
                          className="w-full px-3 py-2.5 border border-[#282828]/12 text-[13px] text-[#282828] resize-y bg-transparent"
                          data-testid="sms-body"
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] uppercase tracking-[0.08em] text-[#676664]/60 mb-1.5">
                          Review link template — placeholders: <code>{"{site_url}"}</code>, <code>{"{outfit_id}"}</code>, <code>{"{booking_id}"}</code>
                        </label>
                        <Input
                          value={settings.review_link_template || ""}
                          onChange={(e) => setSettings({ ...settings, review_link_template: e.target.value })}
                          className="text-[13px] font-mono"
                          data-testid="review-link-template"
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Trial Request toggles */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <label className="flex items-center gap-2 cursor-pointer border border-[#282828]/8 p-3 hover:border-[#9A7B4F]/30">
                          <input
                            type="checkbox"
                            checked={!!settings.trial_enabled_email}
                            onChange={(e) => setSettings({ ...settings, trial_enabled_email: e.target.checked })}
                            data-testid="toggle-trial-email"
                            className="w-4 h-4 accent-[#9A7B4F]"
                          />
                          <Mail className="w-4 h-4 text-[#676664]" />
                          <span className="text-[13px] text-[#282828]">Send trial-request email</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer border border-[#282828]/8 p-3 hover:border-[#9A7B4F]/30">
                          <input
                            type="checkbox"
                            checked={!!settings.trial_enabled_sms}
                            onChange={(e) => setSettings({ ...settings, trial_enabled_sms: e.target.checked })}
                            data-testid="toggle-trial-sms"
                            className="w-4 h-4 accent-[#9A7B4F]"
                          />
                          <MessageSquare className="w-4 h-4 text-[#676664]" />
                          <span className="text-[13px] text-[#282828]">Send trial-request SMS</span>
                        </label>
                      </div>

                      <div>
                        <label className="block text-[11px] uppercase tracking-[0.08em] text-[#676664]/60 mb-1.5">Trial email subject</label>
                        <Input
                          value={settings.trial_email_subject || ""}
                          onChange={(e) => setSettings({ ...settings, trial_email_subject: e.target.value })}
                          className="text-[13px]"
                          data-testid="trial-email-subject"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] uppercase tracking-[0.08em] text-[#676664]/60 mb-1.5">Trial email body</label>
                        <textarea
                          rows={6}
                          value={settings.trial_email_body || ""}
                          onChange={(e) => setSettings({ ...settings, trial_email_body: e.target.value })}
                          className="w-full px-3 py-2.5 border border-[#282828]/12 text-[13px] text-[#282828] resize-y bg-transparent"
                          data-testid="trial-email-body"
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] uppercase tracking-[0.08em] text-[#676664]/60 mb-1.5">Trial SMS body</label>
                        <textarea
                          rows={3}
                          value={settings.trial_sms_body || ""}
                          onChange={(e) => setSettings({ ...settings, trial_sms_body: e.target.value })}
                          className="w-full px-3 py-2.5 border border-[#282828]/12 text-[13px] text-[#282828] resize-y bg-transparent"
                          data-testid="trial-sms-body"
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] uppercase tracking-[0.08em] text-[#676664]/60 mb-1.5">
                          Trial link template — placeholders: <code>{"{site_url}"}</code>, <code>{"{trial_lead_id}"}</code>
                        </label>
                        <Input
                          value={settings.trial_link_template || ""}
                          onChange={(e) => setSettings({ ...settings, trial_link_template: e.target.value })}
                          className="text-[13px] font-mono"
                          data-testid="trial-link-template"
                        />
                      </div>
                    </>
                  )}

                  <p className="text-[10px] text-[#676664]/45 leading-relaxed">
                    Available placeholders in subject / body: <code>{"{first_name}"}</code>, <code>{"{review_link}"}</code>.
                    Last updated by <strong>{settings.updated_by || "—"}</strong>
                    {settings.updated_at ? ` on ${new Date(settings.updated_at).toLocaleString("en-AU")}` : ""}.
                  </p>

                  <div>
                    <Button
                      onClick={saveSettings}
                      disabled={saving}
                      className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-[10px] tracking-[0.12em] uppercase"
                      data-testid="save-settings-btn"
                    >
                      {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3 h-3 mr-1.5" /> Save settings</>}
                    </Button>
                  </div>
                </div>
              </div>

              {/* ─── Manual trigger ─── */}
              <div className="bg-white border border-[#282828]/8 p-5 mb-6">
                <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                  <h2 className="text-[14px] text-[#282828] uppercase tracking-[0.08em] font-medium">Manual trigger</h2>
                  <div className="flex items-center gap-2">
                    <label className="text-[10px] uppercase tracking-[0.08em] text-[#676664]/60">Type</label>
                    <select
                      value={requestType}
                      onChange={(e) => setRequestType(e.target.value)}
                      className="border border-[#282828]/12 px-2.5 py-1 text-[11px] bg-white"
                      data-testid="request-type-dropdown"
                    >
                      <option value="review">Review Request</option>
                      <option value="trial">Trial Request</option>
                    </select>
                  </div>
                </div>
                <p className="text-[12px] text-[#676664]/55 mb-3">
                  {requestType === "trial"
                    ? "Enter a trial lead ID and pick which channels to send right now — uses the Trial Request templates above."
                    : "Enter a booking ID and pick which channels to send right now (skips the delay and ignores the enable toggles above for one-off sends)."}
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    placeholder={requestType === "trial" ? "trial lead ID (e.g. lead-abc123) or a linked booking ID" : "booking ID (e.g. abc-123)"}
                    value={manualBookingId}
                    onChange={(e) => setManualBookingId(e.target.value.trim())}
                    className="text-[13px] flex-1"
                    data-testid="manual-booking-id"
                  />
                  <Button
                    onClick={() => sendManual(manualBookingId, ["email"])}
                    disabled={manualSending || !manualBookingId}
                    variant="outline"
                    className="text-[10px] tracking-[0.12em] uppercase border-[#282828]/12 text-[#676664]"
                    data-testid="manual-send-email"
                  >
                    <Mail className="w-3 h-3 mr-1.5" /> Email only
                  </Button>
                  <Button
                    onClick={() => sendManual(manualBookingId, ["sms"])}
                    disabled={manualSending || !manualBookingId}
                    variant="outline"
                    className="text-[10px] tracking-[0.12em] uppercase border-[#282828]/12 text-[#676664]"
                    data-testid="manual-send-sms"
                  >
                    <MessageSquare className="w-3 h-3 mr-1.5" /> SMS only
                  </Button>
                  <Button
                    onClick={() => sendManual(manualBookingId, ["email", "sms"])}
                    disabled={manualSending || !manualBookingId}
                    className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-[10px] tracking-[0.12em] uppercase"
                    data-testid="manual-send-both"
                  >
                    {manualSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Send className="w-3 h-3 mr-1.5" /> Send both</>}
                  </Button>
                </div>
              </div>

              {/* ─── History list ─── */}
              <div className="flex gap-1 mb-3 bg-white border border-[#282828]/8 p-1 w-fit">
                {[
                  { key: "all", label: `All (${counts.pending + counts.sent + counts.failed})` },
                  { key: "pending", label: `Pending (${counts.pending})` },
                  { key: "sent", label: `Sent (${counts.sent})` },
                  { key: "failed", label: `Failed (${counts.failed})` },
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setFilter(tab.key)}
                    className={`px-4 py-1.5 text-[11px] tracking-[0.05em] uppercase transition-all ${filter === tab.key ? "bg-[#9A7B4F] text-white" : "text-[#676664]/60 hover:text-[#282828]"}`}
                    data-testid={`rr-filter-${tab.key}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {loadingList ? (
                <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#9A7B4F]" /></div>
              ) : rows.length === 0 ? (
                <div className="text-center py-12 bg-white border border-[#282828]/8">
                  <ClipboardList className="w-9 h-9 text-[#676664]/15 mx-auto mb-2.5" strokeWidth={1} />
                  <p className="text-[13px] text-[#676664]/40">No review requests in this category.</p>
                </div>
              ) : (
                <div className="bg-white border border-[#282828]/8" data-testid="review-request-rows">
                  {rows.map((r, idx) => (
                    <div key={r.id} className={`p-3 sm:p-4 ${idx !== 0 ? "border-t border-[#282828]/8" : ""} flex items-start gap-3 flex-wrap`}>
                      <div className="flex-shrink-0">
                        {r.channel === "email" ? (
                          <Mail className="w-4 h-4 text-[#676664]/45" />
                        ) : (
                          <MessageSquare className="w-4 h-4 text-[#676664]/45" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[12px] text-[#282828] font-medium">{r.channel.toUpperCase()}</span>
                          {r.request_type === "trial" && (
                            <span className="px-1.5 py-0.5 bg-purple-50 text-purple-700 text-[9px] uppercase tracking-wider rounded inline-flex items-center gap-0.5">
                              <Sparkles className="w-2.5 h-2.5" /> Trial
                            </span>
                          )}
                          <span className="text-[10px] text-[#676664]/40 font-mono">{r.trial_lead_id || r.booking_id}</span>
                          {r.status === "sent" && <span className="px-1.5 py-0.5 bg-green-50 text-green-600 text-[9px] uppercase tracking-wider rounded inline-flex items-center gap-0.5"><Check className="w-2.5 h-2.5" />Sent</span>}
                          {r.status === "pending" && <span className="px-1.5 py-0.5 bg-amber-50 text-amber-700 text-[9px] uppercase tracking-wider rounded">Pending</span>}
                          {r.status === "failed" && <span className="px-1.5 py-0.5 bg-red-50 text-red-600 text-[9px] uppercase tracking-wider rounded inline-flex items-center gap-0.5"><X className="w-2.5 h-2.5" />Failed</span>}
                          <span className="px-1.5 py-0.5 bg-[#FAF8F0] text-[#676664] text-[9px] uppercase tracking-wider rounded">{r.trigger || "auto"}</span>
                        </div>
                        <p className="text-[10px] text-[#676664]/45 mt-0.5">
                          {r.status === "sent" && r.sent_at ? `Sent ${new Date(r.sent_at).toLocaleString("en-AU")}` : null}
                          {r.status === "pending" && r.scheduled_at ? `Scheduled ${new Date(r.scheduled_at).toLocaleString("en-AU")}` : null}
                          {r.status === "failed" ? `Failed${r.error ? ` — ${r.error}` : ""}` : null}
                          {` · attempts ${r.attempts || 0}`}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <p className="mt-6 text-[10px] text-[#676664]/45 leading-relaxed">
                <AlertTriangle className="w-3 h-3 inline mr-1" />
                Mahra does not post anything to Google automatically. Customers can choose to share their
                website review on Google after they submit it — this is initiated from their browser only,
                with a copy-to-clipboard convenience step.
              </p>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
